import { t as globalT, t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { Button, Group, Stack, Switch, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useInsertMutation } from "@supabase-cache-helpers/postgrest-react-query";
import { PostgrestError } from "@supabase/supabase-js";
import { ErrorAlert } from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";
import z from "zod";

import { api } from "@/api.ts";

export const Route = createFileRoute("/customers/create")({
  component: RouteComponent,
  validateSearch: z.object({ next: z.string().startsWith("/").optional() }),
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Create customer`,
      },
    ],
  }),
});

function RouteComponent() {
  const { next } = Route.useSearch();

  const form = useForm({
    initialValues: {
      name: "",
      organisation_number: "",
      is_geodatasamverkan_member: false,
    },
    validate: {
      organisation_number: (value) =>
        /^[0-9]{10}$/.test(value)
          ? null
          : t`Invalid organisation number format`,
      name: (value) => (value.trim().length > 0 ? null : t`Name is required`),
    },
  });

  const { mutateAsync: insert } = useInsertMutation(
    api.from("customer"),
    ["id"],
    "id",
  );
  const navigate = Route.useNavigate();
  const handleSubmit = useCallback<Parameters<(typeof form)["onSubmit"]>[0]>(
    async (values) => {
      try {
        const result = await insert([values]);
        await navigate({
          to: next ?? "/customers/$id",
          params: { id: result?.[0].id },
        });
      } catch (e) {
        if (e instanceof PostgrestError) {
          if (
            e.code === "23505" &&
            new RegExp(
              "Key \\(organisation_number\\)=\\(.*\\) already exists.",
            ).test(e.details)
          ) {
            form.setFieldError(
              "organisation_number",
              t`A customer with this organisation number already exists.`,
            );
          } else {
            form.setFieldError("__root", e.message);
          }
        } else if (e instanceof Error) {
          form.setFieldError("__root", e.message);
        }
      }
    },
    [form, insert, navigate, next],
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <h1>
          <Trans>Create new customer</Trans>
        </h1>
        <TextInput
          label={t`Organisation Number`}
          placeholder="XXXXXXXXXX"
          withAsterisk
          disabled={form.submitting}
          key={form.key("organisation_number")}
          {...form.getInputProps("organisation_number")}
        />
        <TextInput
          label={t`Name`}
          withAsterisk
          disabled={form.submitting}
          key={form.key("name")}
          {...form.getInputProps("name")}
        />
        <Switch
          label={t`Is Geodatasamverkan Member`}
          disabled={form.submitting}
          key={form.key("is_geodatasamverkan_member")}
          {...form.getInputProps("is_geodatasamverkan_member", {
            type: "checkbox",
          })}
        />
        <Group justify="flex-end" mt="md">
          <Button
            type="submit"
            loading={form.submitting}
            disabled={form.submitting}
          >
            <Trans>Create</Trans>
          </Button>
        </Group>
        {form.errors.__root && <ErrorAlert>{form.errors.__root}</ErrorAlert>}
      </Stack>
    </form>
  );
}
